import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { getFromLocalStorage, removeFromLocalStorage } from '../mock';
import { hasPermission, ROLE_LABELS } from '../utils/permissions';
import {
  Users, Briefcase, DollarSign, CheckCircle, Clock, Eye, Plus, Wrench,
  UserCog, Activity, Car, Shield, Calendar as CalIcon,
  MessageCircle, CalendarCheck, BarChart3, Archive, Database, Package, Link2, Send, LogOut, AlertTriangle
} from 'lucide-react';
import { toast } from '../hooks/use-toast';

// Shared utilities
import { BACKEND_URL, getAuthHeaders } from './dashboardUtils';

// Extracted components
import JobCard from './JobCard';
import NotificationBell from './NotificationBell';
import DashboardStatsRow from './DashboardStatsRow';
import CustomersTabContent from './CustomersTabContent';
import VehiclesTabContent from './VehiclesTabContent';
import StaffTabContent from './StaffTabContent';
import PerformanceTabContent from './PerformanceTabContent';
import PaymentModal from './PaymentModal';
import VehicleHistoryModal from './VehicleHistoryModal';
import BranchComparison from './BranchComparison';
import OverviewTab from './OverviewTab';
import JobsTab from './JobsTab';
import LogsTab from './LogsTab';
import { VehicleEditDialog, VehicleTransferDialog } from './VehicleDialogs';
import useAdminHandlers from './useAdminHandlers';

// Existing components
import AddEditCustomer from './AddEditCustomer';
import BranchSelector from './BranchSelector';
import AddEditJob from './AddEditJob';
import InspectionForm from './InspectionForm';
import OperationalCalendar from './OperationalCalendar';
import WhatsAppModule from './WhatsAppModule';
import ControlAppointments from './ControlAppointments';
import ServiceCatalogTab from './ServiceCatalogTab';
import ServiceMappingTab from './ServiceMappingTab';
import ReportsModule from './ReportsModule';
import PaymentsTab from './PaymentsTab';
import ArchiveTab from './ArchiveTab';
import BackupModule from './BackupModule';
import DataIntegrityTab from './DataIntegrityTab';
import StaffForm from './StaffForm';
import ApplicatorWorkQueue from './ApplicatorWorkQueue';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [staffList, setStaffList] = useState([]);
  const [activityLogs, setActivityLogs] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [selectedJob, setSelectedJob] = useState(null);
  const [paymentAmount, setPaymentAmount] = useState('');
  const [paymentNote, setPaymentNote] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('nakit');
  const [paymentType, setPaymentType] = useState('normal');
  const [showAddEditCustomer, setShowAddEditCustomer] = useState(false);
  const [showAddEditJob, setShowAddEditJob] = useState(false);
  const [showStaffModal, setShowStaffModal] = useState(false);
  const [showInspection, setShowInspection] = useState(false);
  const [inspectionJob, setInspectionJob] = useState(null);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [editingJob, setEditingJob] = useState(null);
  const [editingStaff, setEditingStaff] = useState(null);
  const [newJobCustomerId, setNewJobCustomerId] = useState(null);
  const [vehicleHistory, setVehicleHistory] = useState(null);
  const [showVehicleHistory, setShowVehicleHistory] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [vehicleSearchQuery, setVehicleSearchQuery] = useState('');
  const [vehicleSearchResults, setVehicleSearchResults] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showNotifications, setShowNotifications] = useState(false);
  const [editingVehicle, setEditingVehicle] = useState(null);
  const [transferVehicle, setTransferVehicle] = useState(null);
  const [transferTargetId, setTransferTargetId] = useState('');
  const [selectedBranchId, setSelectedBranchId] = useState(() => sessionStorage.getItem('selectedBranchId') || 'all');
  const [dashboardStats, setDashboardStats] = useState(null);
  const [branches, setBranches] = useState([]);
  const [staffPerformance, setStaffPerformance] = useState([]);
  const [activeTab, setActiveTab] = useState(null);
  const [jobsFilter, setJobsFilter] = useState(null);

  // ===== DATA LOADING =====
  const loadData = useCallback(async () => {
    try {
      const h = getAuthHeaders();
      const branchParam = selectedBranchId && selectedBranchId !== 'all' ? `branchId=${selectedBranchId}` : '';
      const [cRes, jRes, sRes, vRes] = await Promise.all([
        fetch(`${BACKEND_URL}/api/customers/${branchParam ? `?${branchParam}` : ''}`, { headers: h }),
        fetch(`${BACKEND_URL}/api/jobs${branchParam ? `?${branchParam}` : ''}`, { headers: h }),
        fetch(`${BACKEND_URL}/api/staff${branchParam ? `?${branchParam}` : ''}`, { headers: h }),
        fetch(`${BACKEND_URL}/api/vehicles`, { headers: h }),
      ]);
      if (cRes.ok) { const d = await cRes.json(); setCustomers(d.customers || []); }
      if (jRes.ok) { const d = await jRes.json(); setJobs(d.jobs || []); }
      if (sRes.ok) { const d = await sRes.json(); setStaffList(d.staff || []); }
      if (vRes.ok) { const d = await vRes.json(); setVehicles(d.vehicles || []); }
      const statsRes = await fetch(`${BACKEND_URL}/api/jobs/dashboard/stats${branchParam ? `?${branchParam}` : ''}`, { headers: h });
      if (statsRes.ok) { setDashboardStats(await statsRes.json()); }
      const brRes = await fetch(`${BACKEND_URL}/api/branches`, { headers: h });
      if (brRes.ok) { const bd = await brRes.json(); setBranches(bd.branches || []); }
    } catch (error) {
      toast({ title: "Veri Yükleme Hatası", description: error.message, variant: "destructive" });
    }
  }, [selectedBranchId]);

  // ===== HANDLERS (extracted to custom hook) =====
  const handlers = useAdminHandlers({
    navigate, jobs, setJobs, customers, setCustomers, staffList, setStaffList,
    setActivityLogs, setNotifications, setUnreadCount,
    setVehicleHistory, setShowVehicleHistory, selectedBranchId, loadData,
  });

  useEffect(() => {
    const u = getFromLocalStorage('currentUser');
    if (!u || u.role === 'customer') { navigate('/login'); return; }
    setUser(u);
    loadData();
    handlers.loadNotifications();
    const interval = setInterval(loadData, 15000);
    const notifInterval = setInterval(handlers.loadNotifications, 30000);
    return () => { clearInterval(interval); clearInterval(notifInterval); };
  }, [navigate, loadData, handlers.loadNotifications]);

  const can = useCallback((perm) => hasPermission(user, perm), [user]);

  // ===== MEMOIZED DERIVED DATA =====
  const jobsByStatus = useCallback((statusList) => jobs.filter(j => statusList.includes(j.status)), [jobs]);
  const activeJobs = useMemo(() => jobs.filter(j => !['teslim_edildi', 'completed'].includes(j.status)), [jobs]);
  const delayedJobs = useMemo(() => jobs.filter(j => j.isDelayed), [jobs]);
  const sortedDelayedJobs = useMemo(() => [...delayedJobs].sort((a, b) => (a.delaySeverity === 'critical' ? -1 : 1)), [delayedJobs]);
  const activeDelayedJobs = useMemo(() => activeJobs.filter(j => j.isDelayed), [activeJobs]);
  const activeNonDelayedJobs = useMemo(() => activeJobs.filter(j => !j.isDelayed), [activeJobs]);
  const completedJobs = useMemo(() => jobsByStatus(['teslim_edildi', 'completed']), [jobsByStatus]);
  const readyCount = useMemo(() => jobsByStatus(['teslime_hazir', 'ready']).length, [jobsByStatus]);
  const inProgressCount = useMemo(() => jobsByStatus(['islemde', 'in_progress']).length, [jobsByStatus]);
  const todayStr = new Date().toISOString().split('T')[0];
  const todayJobs = useMemo(() => jobs.filter(j => j.startDate?.startsWith(todayStr) || j.createdAt?.startsWith(todayStr)), [jobs, todayStr]);

  const stats = useMemo(() => ({
    totalCustomers: customers.length,
    activeJobs: activeJobs.length,
    readyJobs: jobsByStatus(['teslime_hazir', 'ready']).length,
    totalCollected: jobs.reduce((s, j) => s + (j.paidAmount || 0), 0),
    totalRemaining: jobs.reduce((s, j) => s + (j.remainingBalance || 0), 0),
  }), [customers.length, activeJobs.length, jobs, jobsByStatus]);

  const isTechnician = user?.role === 'technician';
  const tabList = useMemo(() => {
    const tabs = [];
    // Uygulamacı rolü icin "Bana Atananlar" ilk tab
    if (isTechnician) tabs.push({ key: 'mywork', label: 'Bana Atananlar', icon: <Wrench className="h-4 w-4" /> });
    tabs.push({ key: 'overview', label: 'Operasyon', icon: <Eye className="h-4 w-4" /> });
    tabs.push({ key: 'calendar', label: 'Takvim', icon: <CalIcon className="h-4 w-4" /> });
    tabs.push({ key: 'jobs', label: 'İşler', icon: <Briefcase className="h-4 w-4" /> });
    if (can('can_view_customers')) tabs.push({ key: 'customers', label: 'Müşteriler', icon: <Users className="h-4 w-4" /> });
    tabs.push({ key: 'vehicles', label: 'Araçlar', icon: <Car className="h-4 w-4" /> });
    if (can('can_view_payments')) tabs.push({ key: 'payments', label: 'Ödemeler', icon: <DollarSign className="h-4 w-4" /> });
    tabs.push({ key: 'whatsapp', label: 'WhatsApp', icon: <MessageCircle className="h-4 w-4" /> });
    tabs.push({ key: 'control', label: 'Kontrol', icon: <CalendarCheck className="h-4 w-4" /> });
    tabs.push({ key: 'reports', label: 'Raporlar', icon: <BarChart3 className="h-4 w-4" /> });
    if (can('can_manage_staff')) tabs.push({ key: 'staff', label: 'Personel', icon: <UserCog className="h-4 w-4" /> });
    if (can('can_manage_staff')) tabs.push({ key: 'performance', label: 'Performans', icon: <BarChart3 className="h-4 w-4" /> });
    if (can('can_view_logs')) tabs.push({ key: 'logs', label: 'Loglar', icon: <Activity className="h-4 w-4" /> });
    const isAdminUser = user?.isAdmin || user?.role === 'admin';
    if (isAdminUser) tabs.push({ key: 'archive', label: 'Arşiv', icon: <Archive className="h-4 w-4" /> });
    if (isAdminUser) tabs.push({ key: 'catalog', label: 'Katalog', icon: <Package className="h-4 w-4" /> });
    if (isAdminUser) tabs.push({ key: 'mapping', label: 'Eşleştirme', icon: <Link2 className="h-4 w-4" /> });
    if (isAdminUser) tabs.push({ key: 'backup', label: 'Yedek', icon: <Database className="h-4 w-4" /> });
    if (isAdminUser) tabs.push({ key: 'data-integrity', label: 'Veri Temizliği', icon: <AlertTriangle className="h-4 w-4" /> });
    return tabs;
  }, [can, user, isTechnician]);

  // Shared callback props for JobCard components
  const jobCallbacks = useMemo(() => ({
    onQuickProgress: handlers.handleQuickProgress,
    onArchive: handlers.handleArchiveJob,
    onDelete: handlers.handleDeleteJob,
    onPayment: (job) => { setSelectedJob(job); setPaymentAmount(''); setPaymentNote(''); setPaymentMethod('nakit'); },
    onEdit: (job) => { setEditingJob(job); setShowAddEditJob(true); },
    onInspect: (job) => { setInspectionJob(job); setShowInspection(true); },
  }), [handlers.handleQuickProgress, handlers.handleArchiveJob, handlers.handleDeleteJob]);

  const handleNotificationClick = (n) => {
    setShowNotifications(false);
    const d = n.data || {};
    if (d.jobId) { const j = jobs.find(x => x.id === d.jobId); if (j) { setEditingJob(j); setShowAddEditJob(true); } }
    else if (d.customerId) { const c = customers.find(x => x.id === d.customerId); if (c) { setEditingCustomer(c); setShowAddEditCustomer(true); } }
    else if (d.staffId) { const s = staffList.find(x => x.id === d.staffId); if (s) { setEditingStaff(s); setShowStaffModal(true); } }
    else if (d.vehicleId) { handlers.handleViewVehicleHistory(d.vehicleId); }
  };

  if (!user) return null;

  const isAdmin = user?.isAdmin || user?.role === 'admin';

  const handleLogout = () => {
    removeFromLocalStorage('accessToken');
    removeFromLocalStorage('currentUser');
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Header */}
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-background/80 hairline-b">
        <div className="max-w-7xl mx-auto px-5 h-14 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <img src="/art-garage-logo.png" alt="ART Garage" className="h-8 w-auto" data-testid="admin-dashboard-title" />
            <span className="hidden sm:inline-block h-5 w-px bg-white/10" />
            <span className="hidden sm:inline font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
              {user.name} . {ROLE_LABELS[user.role] || user.role}
            </span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="hidden md:block">
              <BranchSelector selectedBranchId={selectedBranchId} onBranchChange={(bid) => { setSelectedBranchId(bid); sessionStorage.setItem('selectedBranchId', bid); }} />
            </div>
            <NotificationBell user={user} notifications={notifications} unreadCount={unreadCount}
              showNotifications={showNotifications} setShowNotifications={setShowNotifications}
              onMarkAllRead={handlers.handleMarkAllRead} onLoadNotifications={handlers.loadNotifications}
              onNotificationClick={handleNotificationClick} />
            {can('can_create_customers') && (
              <button onClick={() => { setEditingCustomer(null); setShowAddEditCustomer(true); }}
                className="flex items-center gap-1.5 px-3 h-9 rounded-lg hairline-strong text-sm font-medium hover:bg-white/5 transition-colors"
                style={{ background: 'hsl(0 0% 7%)' }}
                data-testid="add-customer-btn">
                <Users size={13} />
                <Plus size={11} className="text-brand" strokeWidth={3} />
                <span className="hidden sm:inline">Musteri</span>
              </button>
            )}
            {can('can_create_jobs') && (
              <button onClick={() => { setEditingJob(null); setNewJobCustomerId(null); setShowAddEditJob(true); }}
                className="cta-brand flex items-center gap-1.5 px-3 h-9 rounded-lg text-sm"
                data-testid="add-job-btn">
                <Briefcase size={13} />
                <Plus size={11} strokeWidth={3} />
                <span className="hidden sm:inline">Yeni Is</span>
              </button>
            )}
            <button onClick={handleLogout}
              className="w-9 h-9 rounded-lg hairline hover:bg-white/5 flex items-center justify-center text-muted-foreground transition-colors"
              style={{ background: 'hsl(0 0% 7%)' }}
              data-testid="admin-logout-btn"
              aria-label="Cikis Yap">
              <LogOut size={14} />
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-5 pt-8 pb-24 grid-bg-subtle">
        {/* Title Block */}
        <div className="mb-8 fade-up">
          <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-brand mb-2">
            -- Genel Bakis . {new Date().toLocaleDateString('tr-TR', { day: 'numeric', month: 'long', year: 'numeric' })}
          </div>
          <h1 className="font-display font-extrabold text-4xl md:text-5xl tracking-tight leading-[0.95]">
            Bugun ne oluyor?
          </h1>
        </div>

        <DashboardStatsRow stats={stats} dashboardStats={dashboardStats}
          onNavigateToJobs={(filter) => { setJobsFilter(filter); setActiveTab('jobs'); }} />
        <BranchComparison dashboardStats={dashboardStats} selectedBranchId={selectedBranchId} />

        {/* Tabs */}
        <Tabs value={activeTab || (isTechnician ? "mywork" : "overview")} onValueChange={(v) => {
          setActiveTab(v);
          if (v !== 'jobs') setJobsFilter(null);
          if (v === 'logs') handlers.loadLogs();
          if (v === 'performance') {
            const h = getAuthHeaders();
            const branchParam = selectedBranchId && selectedBranchId !== 'all' ? `?branchId=${selectedBranchId}` : '';
            fetch(`${BACKEND_URL}/api/staff/performance/summary${branchParam}`, { headers: h })
              .then(r => r.json()).then(d => setStaffPerformance(d.performance || [])).catch(() => {});
          }
        }}>
          <TabsList className="bg-surface-2 hairline mb-4 flex-wrap h-auto gap-1 p-1">
            {tabList.map(t => (
              <TabsTrigger key={t.key} value={t.key} className="data-[state=active]:bg-brand data-[state=active]:text-black text-muted-foreground text-xs gap-1 px-3 py-1.5">
                {t.icon}{t.label}
              </TabsTrigger>
            ))}
          </TabsList>

          {isTechnician && (
            <TabsContent value="mywork" data-testid="tab-mywork">
              <ApplicatorWorkQueue jobs={jobs} user={user} can={can} jobCallbacks={jobCallbacks} />
            </TabsContent>
          )}

          <TabsContent value="overview">
            <OverviewTab jobs={jobs} can={can}
              todayJobs={todayJobs} readyCount={readyCount} inProgressCount={inProgressCount} jobCallbacks={jobCallbacks} />
          </TabsContent>

          <TabsContent value="calendar" data-testid="tab-calendar">
            <OperationalCalendar jobs={jobs} staffList={staffList} currentUserId={user?.id || user?.staffId}
              onEditJob={(job) => { setEditingJob(job); setShowAddEditJob(true); }} />
          </TabsContent>

          <TabsContent value="jobs">
            <JobsTab activeJobs={activeJobs} activeDelayedJobs={activeDelayedJobs} activeNonDelayedJobs={activeNonDelayedJobs}
              completedJobs={completedJobs} can={can} jobCallbacks={jobCallbacks}
              filter={jobsFilter} onClearFilter={() => setJobsFilter(null)} />
          </TabsContent>

          {can('can_view_customers') && (
            <TabsContent value="customers" data-testid="tab-customers">
              <CustomersTabContent customers={customers} jobs={jobs} vehicles={vehicles}
                searchQuery={searchQuery} setSearchQuery={setSearchQuery} can={can} user={user}
                onSendActivation={handlers.handleSendActivation} onResendActivation={handlers.handleResendActivation}
                onWhatsAppActivation={handlers.handleWhatsAppActivation} onAdminSetPassword={handlers.handleAdminSetPassword}
                onImpersonate={handlers.handleImpersonateCustomer}
                onAddJob={(customerId) => { setNewJobCustomerId(customerId); setEditingJob(null); setShowAddEditJob(true); }}
                onEdit={(c) => { setEditingCustomer(c); setShowAddEditCustomer(true); }}
                onDelete={handlers.handleDeleteCustomer} onViewVehicleHistory={handlers.handleViewVehicleHistory} />
            </TabsContent>
          )}

          <TabsContent value="vehicles" data-testid="tab-vehicles">
            <VehiclesTabContent vehicles={vehicles} customers={customers} jobs={jobs}
              vehicleSearchQuery={vehicleSearchQuery} vehicleSearchResults={vehicleSearchResults}
              can={can} user={user} onSearch={(q) => handlers.handleVehicleSearch(q, setVehicleSearchQuery, setVehicleSearchResults)}
              onViewHistory={handlers.handleViewVehicleHistory}
              onEdit={(v) => setEditingVehicle(v)}
              onTransfer={(v) => { setTransferVehicle(v); setTransferTargetId(''); }}
              onDelete={handlers.handleDeleteVehicle} />
          </TabsContent>

          {can('can_view_payments') && (
            <TabsContent value="payments" data-testid="tab-payments"><PaymentsTab can={can} customers={customers} /></TabsContent>
          )}
          <TabsContent value="whatsapp" data-testid="tab-whatsapp"><WhatsAppModule customers={customers} jobs={jobs} /></TabsContent>
          <TabsContent value="control" data-testid="tab-control"><ControlAppointments /></TabsContent>
          <TabsContent value="reports" data-testid="tab-reports"><ReportsModule /></TabsContent>

          {can('can_manage_staff') && (
            <TabsContent value="staff" data-testid="tab-staff">
              <StaffTabContent staffList={staffList}
                onAdd={() => { setEditingStaff(null); setShowStaffModal(true); }}
                onEdit={(s) => { setEditingStaff(s); setShowStaffModal(true); }}
                onDelete={handlers.handleDeleteStaff} />
            </TabsContent>
          )}

          {can('can_manage_staff') && (
            <TabsContent value="performance" data-testid="tab-performance">
              <PerformanceTabContent staffPerformance={staffPerformance} />
            </TabsContent>
          )}

          {can('can_view_logs') && (
            <TabsContent value="logs">
              <LogsTab activityLogs={activityLogs} />
            </TabsContent>
          )}

          {isAdmin && <TabsContent value="archive" data-testid="tab-archive"><ArchiveTab /></TabsContent>}
          {isAdmin && <TabsContent value="catalog" data-testid="tab-catalog"><ServiceCatalogTab /></TabsContent>}
          {isAdmin && <TabsContent value="mapping" data-testid="tab-mapping"><ServiceMappingTab /></TabsContent>}
          {isAdmin && <TabsContent value="backup" data-testid="tab-backup"><BackupModule /></TabsContent>}
          {isAdmin && <TabsContent value="data-integrity" data-testid="tab-data-integrity"><DataIntegrityTab /></TabsContent>}
        </Tabs>
      </main>

      {/* MODALS */}
      <PaymentModal selectedJob={selectedJob} paymentAmount={paymentAmount} paymentNote={paymentNote}
        paymentMethod={paymentMethod} paymentType={paymentType}
        setPaymentAmount={setPaymentAmount} setPaymentNote={setPaymentNote}
        setPaymentMethod={setPaymentMethod} setPaymentType={setPaymentType}
        onClose={() => setSelectedJob(null)}
        onSubmit={() => handlers.handleAddPayment(selectedJob, paymentAmount, paymentMethod, paymentNote, paymentType, () => {
          setPaymentAmount(''); setPaymentNote(''); setPaymentMethod('nakit'); setPaymentType('normal'); setSelectedJob(null);
        })} />

      <VehicleHistoryModal vehicleHistory={vehicleHistory} open={showVehicleHistory}
        onClose={() => setShowVehicleHistory(false)} />

      <VehicleEditDialog editingVehicle={editingVehicle} setEditingVehicle={setEditingVehicle}
        onSave={(data) => handlers.handleEditVehicle(data, editingVehicle, setEditingVehicle)} />

      <VehicleTransferDialog transferVehicle={transferVehicle} setTransferVehicle={setTransferVehicle}
        transferTargetId={transferTargetId} setTransferTargetId={setTransferTargetId}
        customers={customers} onTransfer={() => handlers.handleTransferVehicle(transferVehicle, transferTargetId, setTransferVehicle, setTransferTargetId)} />

      <Dialog open={showStaffModal} onOpenChange={() => { setShowStaffModal(false); setEditingStaff(null); }}>
        <DialogContent className="bg-gray-900 border-gray-700 max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white">{editingStaff ? 'Personel Düzenle' : 'Yeni Personel'}</DialogTitle>
          </DialogHeader>
          <StaffForm staff={editingStaff} onSave={() => { setShowStaffModal(false); setEditingStaff(null); loadData(); }} onCancel={() => { setShowStaffModal(false); setEditingStaff(null); }} />
        </DialogContent>
      </Dialog>

      <AddEditCustomer isOpen={showAddEditCustomer} onClose={() => { setShowAddEditCustomer(false); setEditingCustomer(null); }}
        customer={editingCustomer} selectedBranchId={selectedBranchId} branches={branches}
        onSave={() => { setShowAddEditCustomer(false); setEditingCustomer(null); loadData(); }} />

      <AddEditJob isOpen={showAddEditJob} onClose={() => { setShowAddEditJob(false); setEditingJob(null); }}
        job={editingJob} customerId={newJobCustomerId} defaultCustomerId={newJobCustomerId} customers={customers} staffList={staffList}
        userPermissions={user?.permissions || []} branches={branches} selectedBranchId={selectedBranchId}
        onSave={() => { setShowAddEditJob(false); setEditingJob(null); loadData(); }} />

      <InspectionForm isOpen={showInspection} onClose={() => { setShowInspection(false); setInspectionJob(null); }}
        job={inspectionJob} onSaved={() => { setShowInspection(false); setInspectionJob(null); loadData(); }} />
    </div>
  );
};

export default AdminDashboard;
